import { authFetch, getAuthHeaders } from '../lib/authFetch';

const getBaseUrl = (url: string) => url.startsWith('http') ? url : `https://${url}`;
const BASE = import.meta.env.VITE_API_BASE_URL
    ? `${getBaseUrl(import.meta.env.VITE_API_BASE_URL)}/api/p2p`
    : 'http://localhost:5000/api/p2p';

async function req<T>(method: string, path: string, body?: object): Promise<T> {
    const res = await authFetch(`${BASE}${path}`, {
        method,
        headers: getAuthHeaders(),
        body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Request failed');
    return data.data as T;
}

// Settings
export const getP2PSettings = () => req<any>('GET', '/settings/fees');
export const getPaymentTypes = () => req<any[]>('GET', '/settings/payment-types');
export const getMarketPrice = (crypto: string, fiat: string) =>
    req<{ price: number }>('GET', `/market-price?crypto=${crypto}&fiat=${fiat}`);

// Payment Methods
export const getMyPaymentMethods = () => req<any[]>('GET', '/payment-methods');
export const createPaymentMethod = (body: object) => req<any>('POST', '/payment-methods', body);
export const updatePaymentMethod = (id: string, body: object) => req<any>('PUT', `/payment-methods/${id}`, body);
export const deletePaymentMethod = (id: string) => req<any>('DELETE', `/payment-methods/${id}`);
export const setDefaultPaymentMethod = (id: string) => req<any>('PATCH', `/payment-methods/${id}/default`);

// Ads
export const getAds = (params: Record<string, string>) => {
    const q = new URLSearchParams(params).toString();
    return req<any>('GET', `/ads?${q}`);
};
export const getMyAds = () => req<any[]>('GET', '/ads/my');
export const createAdDraft = (body: object) => req<any>('POST', '/ads/draft', body);
export const updateAdStep2 = (id: string, body: object) => req<any>('PATCH', `/ads/${id}/step2`, body);
export const updateAdStep3 = (id: string, body: object) => req<any>('PATCH', `/ads/${id}/step3`, body);
export const publishAd = (id: string) => req<any>('POST', `/ads/${id}/publish`);
export const toggleAd = (id: string) => req<any>('PATCH', `/ads/${id}/toggle`);
export const takeABreak = (pause: boolean) => req<any>('PATCH', '/ads/take-a-break', { pause });
export const deleteAd = (id: string) => req<any>('DELETE', `/ads/${id}`);

// Orders
export const createOrder = (body: object) => req<any>('POST', '/orders', body);
export const getMyOrders = (params?: Record<string, string>) => {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return req<any>('GET', `/orders${q}`);
};
export const getOrderDetail = (id: string) => req<any>('GET', `/orders/${id}`);
export const uploadPaymentProof = (id: string, images: string[]) =>
    req<any>('POST', `/orders/${id}/proof`, { images });
export const markPaymentSent = (id: string, confirmedOwnAccount: boolean) =>
    req<any>('POST', `/orders/${id}/pay`, { confirmedOwnAccount });
export const releaseCrypto = (id: string) => req<any>('POST', `/orders/${id}/release`);
export const cancelOrder = (id: string, reason?: string) =>
    req<any>('POST', `/orders/${id}/cancel`, { reason });
export const requestCancellation = (id: string, reason: string) =>
    req<any>('POST', `/orders/${id}/cancel-request`, { reason });
export const respondCancellation = (id: string, accept: boolean) =>
    req<any>('POST', `/orders/${id}/cancel-request/respond`, { accept });
export const openDispute = (id: string, body: object) =>
    req<any>('POST', `/orders/${id}/dispute`, body);
export const respondDispute = (id: string, body: object) =>
    req<any>('POST', `/orders/${id}/dispute/respond`, body);
export const rateOrder = (id: string, body: object) => req<any>('POST', `/orders/${id}/rate`, body);

// Profile
export const getMyP2PProfile = () => req<any>('GET', '/profile/me');
export const updateMyP2PProfile = (body: object) => req<any>('PUT', '/profile/me', body);
export const getUserP2PProfile = (userId: string) => req<any>('GET', `/profile/${userId}`);
export const acceptP2PDisclaimer = () => req<any>('POST', '/profile/disclaimer');
export const followUser = (id: string) => req<any>('POST', `/profile/follow/${id}`);
export const blockUser = (id: string) => req<any>('POST', `/profile/block/${id}`);
